import { http } from "Utils/http";
import { STATUSES } from "Utils/constants";
import { httpDownload } from "Utils/httpDownload";

export const WA_INIT_URL = "/api/report/analytic/";
export const WA_VALIDATE_URL = "/api/report/analytic/validate";
export const WA_GENERATE_URL = "/api/report/analytic/generate";

const buildReportTypeChildrenCheckBoxes = (handleCheckboxChange) => [
  { id: "DOCUMENT_DOWNLOAD_LOG", name: "reportTypeChildren", value: "DOCUMENT_DOWNLOAD_LOG", label: "Document Download Log", isChecked: false, onChange: handleCheckboxChange, disabled: false },
  { id: "INTERACTIVE_REPORT_ACCESS_LOG", name: "reportTypeChildren", value: "INTERACTIVE_REPORT_ACCESS_LOG", label: "Interactive Report Access Log", isChecked: false, onChange: handleCheckboxChange, disabled: false },
  { id: "PSOPPC_LOGIN_COUNT", name: "reportTypeChildren", value: "PSOPPC_LOGIN_COUNT", label: "PSOPPC Login Count", isChecked: false, onChange: handleCheckboxChange, disabled: false },
  { id: "SOLR_SEARCH_LOG", name: "reportTypeChildren", value: "SOLR_SEARCH_LOG", label: "Solr Search Log", isChecked: false, onChange: handleCheckboxChange, disabled: false }
];

const buildReportTypeOtherCheckBoxes = (handleCheckboxChange, emailSubscriptionListLabel) => [
  { id: "ACCOUNT_DISABLE_LOG", name: "reportTypeOther", value: "ACCOUNT_DISABLE_LOG", label: "Account Disable Log", isChecked: false, onChange: handleCheckboxChange, disabled: false },
  { id: "CHATBOT_LOG", name: "reportTypeOther", value: "CHATBOT_LOG", label: "Chatbot Log", isChecked: false, onChange: handleCheckboxChange, disabled: false },
  { id: "EMAIL_SUBSCRIPTION_LIST", name: "reportTypeOther", value: "EMAIL_SUBSCRIPTION_LIST", label: emailSubscriptionListLabel ?? "Email Subscription List", isChecked: false, onChange: handleCheckboxChange, disabled: false },
  { id: "GOOGLE_RECAPTCHA_LOG", name: "reportTypeOther", value: "GOOGLE_RECAPTCHA_LOG", label: "Google ReCAPTCHA Log", isChecked: false, onChange: handleCheckboxChange, disabled: false },
  { id: "LOGIN_ACTIVITY_LOG", name: "reportTypeOther", value: "LOGIN_ACTIVITY_LOG", label: "Login Activity Log", isChecked: false, onChange: handleCheckboxChange, disabled: false },
  { id: "PUBLIC_FEEDBACK_LOG", name: "reportTypeOther", value: "PUBLIC_FEEDBACK_LOG", label: "Public Feedback Log", isChecked: false, onChange: handleCheckboxChange, disabled: false },
  { id: "REPORT_GENERATION_LOG", name: "reportTypeOther", value: "REPORT_GENERATION_LOG", label: "Report Generation Log", isChecked: false, onChange: handleCheckboxChange, disabled: false },
  { id: "ZENDESK_LOGIN_COUNT", name: "reportTypeOther", value: "ZENDESK_LOGIN_COUNT", label: "Zendesk Login Count", isChecked: false, onChange: handleCheckboxChange, disabled: false }
];

const loadWAFormInfo = async (
  ACTIONS,
  formInfoDispatch,
  handleDateChange,
  handleTextChange,
  handleCheckboxChange,
  emailSubscriptionListLabel,
  token
) => {
  formInfoDispatch({
    type: ACTIONS.SET_INITIAL_DATA,
    data: "",
    loading: true
  });

  let status = STATUSES.SUCCESS;

  await http
    .post(
      WA_INIT_URL,
      {},
      {
        headers: {
          Authorization: token
        }
      }
    )
    .then((res) => {
      const payload = res?.data ?? res;
      if (res?.status !== 1 || (res?.globalErrorMessageList?.length > 0)) {
        status = STATUSES.ERROR;
        return Promise.reject({ status, data: res });
      }
      const data = {
        startDate: {
          value: "",
          validation: {
            required: true,
            isInvalid: false,
            errors: []
          },
          onChange: handleDateChange,
          disabled: false
        },
        endDate: {
          value: "",
          validation: {
            required: true,
            isInvalid: false,
            errors: []
          },
          onChange: handleDateChange,
          disabled: false
        },
        fileName: {
          value: "Website Analytics Report",
          validation: {
            required: true,
            isInvalid: false,
            errors: []
          },
          onChange: handleTextChange,
          disabled: false
        },
        reportTypeChildren: {
          type: "checkbox",
          checkBoxes: buildReportTypeChildrenCheckBoxes(handleCheckboxChange),
          validation: {
            required: true,
            isInvalid: false,
            errors: []
          },
          disabled: false
        },
        reportTypeOther: {
          type: "checkbox",
          checkBoxes: buildReportTypeOtherCheckBoxes(handleCheckboxChange, emailSubscriptionListLabel),
          validation: {
            required: true,
            isInvalid: false,
            errors: []
          },
          disabled: false
        },
        reportTypeError: {
          validation: {
            required: false,
            isInvalid: false,
            errors: []
          }
        }
      };

      formInfoDispatch({
        type: ACTIONS.SET_INITIAL_DATA,
        data: data,
        loading: false
      });
    })
    .catch(function (error) {
      status = STATUSES.ERROR;
      return Promise.reject({ status: status, data: error });
    });
};

const validateWARpt = async (
  token,
  formInfo,
  nameConverter,
  addErrorMessages,
  clearErrorMessages,
  getFieldValue
) => {
  // Clear any old inline errors first
  clearErrorMessages(formInfo.data);

  const body = {
    startDate: getFieldValue("startDate", formInfo.data),
    endDate: getFieldValue("endDate", formInfo.data),
    reportSelection: [
      ...getFieldValue("reportTypeChildren", formInfo.data),
      ...getFieldValue("reportTypeOther", formInfo.data)
    ],
    reportFileName: getFieldValue("fileName", formInfo.data)
  };

  let status = STATUSES.SUCCESS;

  const res = await http
    .post(WA_VALIDATE_URL, body, {
      headers: {
        Authorization: token
      }
    })
    .then((res) => {
      // res is assumed to be server payload (interceptor returns res.data)
      // Map server fieldErrors -> UI fields using nameConverter
      // Example server keys: startDate, endDate, reportFileName
      let x = 0;
      for (const [serverKey, uiKey] of Object.entries(nameConverter)) {
        const fieldErrObj = res.fieldErrors?.[serverKey];
        if (fieldErrObj) {
          const isFirst = x === 0;
          addErrorMessages(uiKey, serverKey, res, isFirst);
          status = STATUSES.ERROR;
          x = x + 1;
        }
      }

      return Promise.resolve({ status: status, data: res });
    })
    .catch(function (error) {
      status = STATUSES.ERROR;
      return Promise.reject({ status: status, data: error });
    });

  return res;
};

const generateWARpt = async (formInfo, token, getFieldValue) => {
  let status = STATUSES.SUCCESS;

  const body = {
    startDate: getFieldValue("startDate", formInfo.data),
    endDate: getFieldValue("endDate", formInfo.data),
    reportSelection: [
      ...getFieldValue("reportTypeChildren", formInfo.data),
      ...getFieldValue("reportTypeOther", formInfo.data)
    ],
    reportFileName: getFieldValue("fileName", formInfo.data)
  };

  const res = await httpDownload
    .post(WA_GENERATE_URL, body, {
      responseType: "blob",
      headers: {
        Authorization: token
      }
    })
    .then((res) => {
      return Promise.resolve({ status: status, data: res });
    })
    .catch(function (error) {
      status = STATUSES.ERROR;
      return Promise.reject({ status: status, data: error });
    });

  return res;
};

export { loadWAFormInfo, validateWARpt, generateWARpt };
